import axios, { AxiosError, AxiosInstance } from 'axios';

// Determine API URL based on environment
const getApiUrl = (): string => {
  // If explicitly set, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In production, use the backend URL (Railway sets this)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // For Railway: if frontend is at grammarhug-frontend-xxx.railway.app,
  // backend is at grammarhug-backend-xxx.railway.app
  if (typeof window !== 'undefined' && window.location.hostname.includes('railway.app')) {
    // Replace 'frontend' with 'backend' in the hostname
    const backendHost = window.location.hostname.replace('-frontend', '-backend').replace('frontend', 'backend');
    return `https://${backendHost}`;
  }

  // Default for local development
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 180000, // 3 minutes for LLM requests (parallel chunks can take time)
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Token is set by auth store
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Don't try to refresh for auth endpoints (prevents infinite loops)
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');

    // If 401, not an auth endpoint, and we haven't already retried
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Import auth store dynamically to avoid circular deps
        const { useAuthStore } = await import('../stores/authStore');
        const refreshed = await useAuthStore.getState().refreshTokens();

        if (refreshed) {
          // Retry the original request
          const token = useAuthStore.getState().accessToken;
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        } else {
          // Refresh failed - redirect to login
          window.location.href = '/login';
        }
      } catch (refreshError) {
        // Logout on refresh failure
        const { useAuthStore } = await import('../stores/authStore');
        await useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Analysis API
export const analysisApi = {
  analyze: (text: string) => api.post('/analysis', { text }),
  getHistory: (page = 1, pageSize = 20) =>
    api.get('/analysis/history', { params: { page, page_size: pageSize } }),
  getById: (id: number) => api.get(`/analysis/${id}`),
  delete: (id: number) => api.delete(`/analysis/${id}`),
  getStats: () => api.get('/analysis/stats'),
};

// User API
export const userApi = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data: { email?: string; full_name?: string }) =>
    api.patch('/users/me', data),
  getStats: () => api.get('/users/me/stats'),
};

// Tier API
export const tierApi = {
  getAll: () => api.get('/tiers'),
  getById: (id: number) => api.get(`/tiers/${id}`),
};

// Export API
export const exportApi = {
  exportTxt: (id: number, includeCorrections = true) =>
    api.get(`/export/${id}/txt`, {
      params: { include_corrections: includeCorrections },
      responseType: 'blob',
    }),
  exportDocx: (id: number, includeCorrections = true) =>
    api.get(`/export/${id}/docx`, {
      params: { include_corrections: includeCorrections },
      responseType: 'blob',
    }),
  exportPdf: (id: number, includeCorrections = true) =>
    api.get(`/export/${id}/pdf`, {
      params: { include_corrections: includeCorrections },
      responseType: 'blob',
    }),
};

// Admin API
export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUserStats: (days = 30) => api.get('/admin/stats/users', { params: { days } }),
  getAnalysisStats: (days = 30) =>
    api.get('/admin/stats/analyses', { params: { days } }),
  getLLMStats: () => api.get('/admin/stats/llm'),
  getSystemHealth: () => api.get('/admin/system/health'),
  runCleanup: (days = 90) => api.post('/admin/cleanup', null, { params: { days } }),

  // Users
  getUsers: (params: {
    skip?: number;
    limit?: number;
    search?: string;
    is_active?: boolean;
    tier_id?: number;
  }) => api.get('/users', { params }),
  getUser: (id: number) => api.get(`/users/${id}`),
  updateUser: (
    id: number,
    data: {
      email?: string;
      full_name?: string;
      is_verified?: boolean;
      is_admin?: boolean;
      is_active?: boolean;
      tier_id?: number;
    }
  ) => api.patch(`/users/${id}`, data),
  deleteUser: (id: number) => api.delete(`/users/${id}`),
  resetUserUsage: (id: number) => api.post(`/users/${id}/reset-usage`),
  getUserStatsById: (id: number) => api.get(`/users/${id}/stats`),

  // Tiers
  createTier: (data: any) => api.post('/tiers', data),
  updateTier: (id: number, data: any) => api.patch(`/tiers/${id}`, data),
  deleteTier: (id: number) => api.delete(`/tiers/${id}`),

  // LLM Config
  getLLMConfigs: () => api.get('/llm'),
  createLLMConfig: (data: any) => api.post('/llm', data),
  updateLLMConfig: (id: number, data: any) => api.patch(`/llm/${id}`, data),
  deleteLLMConfig: (id: number) => api.delete(`/llm/${id}`),
  getLLMHealth: () => api.get('/llm/health'),

  // Cost tracking
  getCostSummary: (startDate?: string, endDate?: string) =>
    api.get('/admin/stats/costs', { params: { start_date: startDate, end_date: endDate } }),
  getCostsByPeriod: (period = 'day', startDate?: string, endDate?: string, limit = 30) =>
    api.get('/admin/stats/costs/by-period', {
      params: { period, start_date: startDate, end_date: endDate, limit },
    }),
  getCostsByUser: (startDate?: string, endDate?: string, limit = 20) =>
    api.get('/admin/stats/costs/by-user', {
      params: { start_date: startDate, end_date: endDate, limit },
    }),
  getCostsByModel: (startDate?: string, endDate?: string) =>
    api.get('/admin/stats/costs/by-model', {
      params: { start_date: startDate, end_date: endDate },
    }),
  getCostDashboard: (days = 30) => api.get('/admin/stats/costs/dashboard', { params: { days } }),

  // Error tracking
  getErrorLogs: (params: {
    error_type?: string;
    provider?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }) => api.get('/admin/stats/errors', { params }),
  getErrorBreakdown: (startDate?: string, endDate?: string) =>
    api.get('/admin/stats/errors/breakdown', {
      params: { start_date: startDate, end_date: endDate },
    }),

  // Model selection settings
  getModelSelection: () => api.get('/admin/settings/models'),
  updateModelSelection: (data: {
    default_model_id?: number | null;
    backup_model_1_id?: number | null;
    backup_model_2_id?: number | null;
    backup_model_3_id?: number | null;
  }) => api.put('/admin/settings/models', null, { params: data }),

  // Backup call stats
  getBackupCallStats: (days = 30, limit = 100) =>
    api.get('/admin/stats/backup-calls', { params: { days, limit } }),
  getBackupCallsByModel: (days = 30) =>
    api.get('/admin/stats/backup-calls/by-model', { params: { days } }),

  // Model cleanup
  removeModelsWithoutPricing: () => api.delete('/admin/settings/models/without-pricing'),

  // Parallel processing stats
  getParallelProcessingStats: (days = 30) =>
    api.get('/admin/stats/parallel-processing', { params: { days } }),
  getProcessingLogs: (params?: {
    skip?: number;
    limit?: number;
    parallel_only?: boolean;
    errors_only?: boolean;
  }) => api.get('/admin/stats/processing-logs', { params }),
};

// Prompts API
export const promptsApi = {
  getAll: (params?: { skip?: number; limit?: number; prompt_type?: string; include_inactive?: boolean }) =>
    api.get('/prompts', { params }),
  getById: (id: number) => api.get(`/prompts/${id}`),
  create: (data: { name: string; prompt_type: string; content: string; description?: string; is_active?: boolean }) =>
    api.post('/prompts', data),
  update: (
    id: number,
    data: {
      name?: string;
      prompt_type?: string;
      content?: string;
      description?: string;
      is_active?: boolean;
      change_description?: string;
    }
  ) => api.patch(`/prompts/${id}`, data),
  revert: (id: number, versionNumber: number, changeDescription?: string) =>
    api.post(`/prompts/${id}/revert/${versionNumber}`, { change_description: changeDescription }),
  delete: (id: number) => api.delete(`/prompts/${id}`),
};

// Models API
export const modelsApi = {
  getAvailable: () => api.get('/models/available'),
  getAvailableByProvider: (provider: string) => api.get(`/models/available/${provider}`),
  getConfigs: () => api.get('/models/configs'),
  syncModels: () => api.post('/models/sync'),
  updatePricing: (configId: number, inputPrice?: string, outputPrice?: string) =>
    api.patch(`/models/configs/${configId}/pricing`, {
      cost_per_1k_input_tokens: inputPrice,
      cost_per_1k_output_tokens: outputPrice,
    }),
  updateAllPricing: () => api.post('/models/configs/update-all-pricing'),
};

// Subscription API
export const subscriptionApi = {
  // Current subscription status
  getCurrent: () => api.get('/subscriptions/current'),

  // Available tiers
  getTiers: () => api.get('/subscriptions/tiers'),

  // Start a new subscription
  subscribe: (tierId: number, interval: string = 'weekly') =>
    api.post(`/subscriptions/subscribe/${tierId}`, null, { params: { interval } }),

  // Upgrade to higher tier
  upgrade: (tierId: number, interval: string = 'weekly') =>
    api.post(`/subscriptions/upgrade/${tierId}`, null, { params: { interval } }),

  // Downgrade to lower tier
  downgrade: (tierId: number, interval: string = 'weekly') =>
    api.post(`/subscriptions/downgrade/${tierId}`, null, { params: { interval } }),

  // Cancel subscription
  cancel: () => api.post('/subscriptions/cancel'),

  // Reactivate cancelled subscription
  reactivate: () => api.post('/subscriptions/reactivate'),

  // Payment history
  getPaymentHistory: (skip = 0, limit = 20) =>
    api.get('/payments/history', { params: { skip, limit } }),

  // Create one-time payment
  createOneTimePayment: (amount: number, description: string) =>
    api.post('/payments/one-time', { amount, description }),
};
